import { Button } from '@ophelos/ui/components/button';
import { MarkdownContent } from '@ophelos/ui/components/markdown-content';
import { ScrollArea } from '@ophelos/ui/components/scroll-area';
import { cn } from '@ophelos/ui/lib/utils';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type { AgentConfig } from '../agent-registry';
import { parseSuggestions, stripStreamingSuggestions } from './suggestions';

/** Friendly, human-readable status shown while a tool call is running (Claude-Code style). */
const TOOL_STATUS_LABELS: Record<string, string> = {
  create_github_issue: 'Creating ticket',
  add_github_issue_comment: 'Adding your note',
  update_github_issue_status: 'Updating ticket',
};

export interface ToolCall {
  id: string;
  tool_name: string;
  tool_args?: Record<string, unknown> | string;
  result?: string;
}

export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  toolCalls?: ToolCall[];
}

export interface StreamingState {
  active: boolean;
  content: string;
  toolCalls?: ToolCall[];
}

interface ChatMessagesProps {
  messages: ChatMessage[];
  streaming?: StreamingState | null;
  agentConfig?: AgentConfig;
  /** Transform message content before rendering (e.g., strip XML blocks) */
  transformContent?: (content: string, role: string) => string;
  /** Custom class for user message bubbles */
  userMessageClassName?: string;
  /** Content shown when there are no messages and not streaming */
  emptyState?: ReactNode;
  /** Custom loading indicator (replaces bouncing dots) */
  loadingElement?: ReactNode;
  /** Content rendered above messages (e.g., lifecycle flows) */
  headerContent?: ReactNode;
  /** Custom renderer for assistant message content (replaces default MarkdownContent) */
  renderAssistantContent?: (content: string) => ReactNode;
  /** Handler for a quick-reply button click. When set, `[[suggestions: …]]` markers render as buttons. */
  onSuggestion?: (text: string) => void;
}

export function ChatMessages({
  messages,
  streaming,
  agentConfig,
  transformContent,
  userMessageClassName,
  emptyState,
  loadingElement,
  headerContent,
  renderAssistantContent,
  onSuggestion,
}: ChatMessagesProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const firstAssistantIdx = messages.findIndex((m) => m.role === 'assistant');

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll to bottom when messages or streaming content change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, streaming?.content]);

  if (messages.length === 0 && !streaming?.active) {
    return <>{emptyState ?? <div className="flex-1" />}</>;
  }

  const renderAssistant = (content: string) => {
    if (renderAssistantContent) return renderAssistantContent(content);
    return <MarkdownContent className="max-w-[85%] text-sm leading-relaxed">{content}</MarkdownContent>;
  };

  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col gap-4 py-4">
        {headerContent}

        {messages.map((msg, idx) => {
          const transformed = transformContent ? transformContent(msg.content, msg.role) : msg.content;
          const isAssistant = msg.role === 'assistant';
          const { text: content, suggestions } = isAssistant ? parseSuggestions(transformed) : { text: transformed, suggestions: [] };
          // Only the latest assistant message offers quick replies, and only once its run is done.
          const showSuggestions =
            isAssistant && idx === messages.length - 1 && suggestions.length > 0 && !!onSuggestion && !streaming?.active;

          return (
            <div key={msg.id ?? idx} className={cn('flex flex-col', msg.role === 'user' ? 'items-end' : 'items-start')}>
              {isAssistant && agentConfig && idx === firstAssistantIdx && <AgentBadge config={agentConfig} />}
              {msg.role === 'user' ? (
                <div
                  className={cn(
                    'max-w-[85%] text-sm leading-relaxed px-4 py-2.5 rounded-3xl whitespace-pre-wrap',
                    userMessageClassName ?? 'bg-muted'
                  )}
                >
                  {content}
                </div>
              ) : (
                renderAssistant(content)
              )}
              {msg.toolCalls && msg.toolCalls.length > 0 && (
                <div className="mt-1 space-y-1 max-w-[85%]">
                  {msg.toolCalls.map((tc) => (
                    <div key={tc.id} className="text-sm  bg-card border rounded-md px-2.5 py-1.5">
                      <span className="font-mono text-foreground">{tc.tool_name}</span>
                      {tc.result && <span className="ml-1 text-foreground/70">→ {tc.result.slice(0, 100)}</span>}
                    </div>
                  ))}
                </div>
              )}
              {showSuggestions && <SuggestionButtons suggestions={suggestions} onSuggestion={onSuggestion} />}
            </div>
          );
        })}

        {streaming?.active && (
          <>
            {firstAssistantIdx === -1 && agentConfig && (
              <div className="flex flex-col items-start">
                <AgentBadge config={agentConfig} />
              </div>
            )}
            {streaming.content && (
              <div className="flex flex-col items-start">{renderAssistant(stripStreamingSuggestions(streaming.content))}</div>
            )}
            <StreamingStatus streaming={streaming} loadingElement={loadingElement} />
          </>
        )}

        <div ref={bottomRef} />
      </div>
    </ScrollArea>
  );
}

/** Quick-reply buttons parsed from an agent message's `[[suggestions: …]]` marker. */
function SuggestionButtons({ suggestions, onSuggestion }: { suggestions: string[]; onSuggestion: (text: string) => void }) {
  return (
    <div className="mt-2 flex flex-wrap gap-2 max-w-[85%]">
      {suggestions.map((s) => (
        <Button key={s} variant="outline" size="sm" onClick={() => onSuggestion(s)}>
          {s}
        </Button>
      ))}
    </div>
  );
}

/**
 * A live status line shown for the whole time the agent's run is active, so the user always sees
 * that something is happening — even during the gap where the agent has stopped typing and a tool
 * (e.g. creating the ticket) is executing.
 */
function StreamingStatus({ streaming, loadingElement }: { streaming: StreamingState; loadingElement?: ReactNode }) {
  const inProgress = (streaming.toolCalls ?? []).filter((tc) => !tc.result);
  const activeTool = inProgress.length > 0 ? inProgress[inProgress.length - 1] : undefined;

  // A tool is running — name it ("Creating ticket…").
  if (activeTool) {
    return <StatusLine label={`${TOOL_STATUS_LABELS[activeTool.tool_name] ?? 'Working'}…`} />;
  }

  // Nothing typed yet — the initial "Thinking…" state (or a caller-provided loader).
  if (!streaming.content) {
    if (loadingElement) return <>{loadingElement}</>;
    return <StatusLine label="Thinking…" dots />;
  }

  // The agent has typed something but the run is still active (a tool is executing) — keep a
  // spinner visible so the pause never looks like a hang.
  return <StatusLine label="Working…" />;
}

function StatusLine({ label, dots = false }: { label: string; dots?: boolean }) {
  return (
    <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
      {dots ? (
        <span className="flex gap-1">
          <span className="size-1.5 rounded-full bg-muted-foreground/75 animate-bounce-high" />
          <span className="size-1.5 rounded-full bg-muted-foreground/75 animate-bounce-high [animation-delay:200ms]!" />
          <span className="size-1.5 rounded-full bg-muted-foreground/75 animate-bounce-high [animation-delay:400ms]!" />
        </span>
      ) : (
        <Loader2 className="size-3.5 animate-spin" />
      )}
      <span>{label}</span>
    </div>
  );
}

function AgentBadge({ config }: { config: AgentConfig }) {
  const Icon = config.icon;
  return (
    <div className="flex items-center gap-1 mb-1 text-xs text-muted-foreground">
      <Icon className="size-3" />
      <span>{config.label}</span>
    </div>
  );
}
